import { API, graphqlOperation } from 'aws-amplify';
import moment from 'moment';
import { useCallback, useEffect, useRef } from 'react';
import { onWorkspaceScore } from '../../../graphql/subscriptions';
import { Workspace, WorkspaceScore, WorkspaceScoreItem } from '../../../types/files';
import ObjectUtils from '../../../utils/ObjectUtils';
import useGroupSettingsContext from '../useGroupSettingsContext';
import useWorkspacesContext from '../useWorkspacesContext';
import useWorkspaces from '../../useWorkspaces';
// import useWorkspaces from '../../useWorkspaces';

const useWorkspacesEffects = (userGroup: string | null) => {
  const { groupWorkspaces, setGroupWorkspaces } = useGroupSettingsContext();
  const { workspacesLoaded, workspaces, workspaceScores,
    setWorkspacesLoaded, setWorkspaces, setWorkspaceScores } = useWorkspacesContext();
  const { fetchCurrentUserWorkspaceUserMemberships } = useWorkspaces();

  const workspaceScoreSubscriptionRef = useRef<any>(null);
  const updatedWorkspacesRef = useRef<Workspace[]>([]);

  const parseWorkspaceScores = (scores?: any) => {
    if (typeof scores === 'string') {
      const parsedScores = JSON.parse(scores?.replace('scores', '') || '{}') || {};

      return Object.keys(parsedScores).map(dashboardId => ({
        dashboardId,
        score: parsedScores[dashboardId]
      }));
    } else if (Array.isArray(scores)) {
      return scores;
    }

    return [];
  };

  const subscribeWorkspaceScore = useCallback(async (localWorkspaceScores: any[]) => {
    if (workspaceScoreSubscriptionRef.current)
      workspaceScoreSubscriptionRef.current.unsubscribe();

    if (userGroup) {
      workspaceScoreSubscriptionRef.current = await (API.graphql(
        graphqlOperation(onWorkspaceScore, { group: userGroup })) as any)
          .subscribe({ next: async ({ value }: any) => {
            if (!value?.data?.onWorkspaceScore || !value?.data?.onWorkspaceScore?.scores)
              return;

            const newScores: WorkspaceScoreItem[] = parseWorkspaceScores(value?.data?.onWorkspaceScore?.scores);
            const updatedScores: WorkspaceScore[] = localWorkspaceScores.map(prevScore => {
              if (prevScore.workspaceId === value?.data?.onWorkspaceScore?.workspaceId) {
                const mergedScores: WorkspaceScoreItem[] = [...parseWorkspaceScores(prevScore.scores) ?? []];

                newScores.forEach(newScore => {
                  const existingScoreIndex = mergedScores.findIndex(score => score.dashboardId === newScore.dashboardId);

                  if (existingScoreIndex !== -1) {
                    mergedScores[existingScoreIndex] = newScore;
                  } else {
                    mergedScores.push(newScore);
                  }
                });

                const updatedAt = moment(value?.data?.onWorkspaceScore?.updatedAt);
                const prevUpdatedAt = moment(prevScore.updatedAt);

                return updatedAt.isAfter(prevUpdatedAt)
                  ? { ...prevScore, ...value?.data?.onWorkspaceScore, scores: mergedScores }
                  : { ...prevScore, scores: mergedScores };
            }

            return prevScore;
          });

          if (!localWorkspaceScores.some(prevScore => prevScore.workspaceId === value?.data?.onWorkspaceScore?.workspaceId))
            updatedScores.push({...value?.data?.onWorkspaceScore, scores: newScores});

          setWorkspaceScores(updatedScores);
      }});
  }
  // eslint-disable-next-line
  }, [userGroup]);

  useEffect(() => {
    subscribeWorkspaceScore(workspaceScores);

    return () => {
      workspaceScoreSubscriptionRef?.current?.unsubscribe();
    }
  }, [workspaceScores, subscribeWorkspaceScore]);

  useEffect(() => {
    if (userGroup)
      Promise.resolve(fetchCurrentUserWorkspaceUserMemberships());
    // eslint-disable-next-line
  }, [userGroup]);

  // useEffect(() => {
  //   if (userGroup && workspacesLoaded) {
  //     computeWorkspaceScores(workspaces, userGroup)
  //       .then((workspaceScoresData) => setWorkspaceScores(workspaceScoresData));
  //   }
  // // eslint-disable-next-line
  // }, [userGroup, workspacesLoaded]);

  useEffect(() => {
    if (userGroup && !!groupWorkspaces.length) {
      if (!ObjectUtils.equalObjects(groupWorkspaces, updatedWorkspacesRef.current)) {
        updatedWorkspacesRef.current = structuredClone(groupWorkspaces || []);
        setWorkspaces(updatedWorkspacesRef.current);
        if (!workspacesLoaded)
          setWorkspacesLoaded(true);
      }
    }
  // eslint-disable-next-line
  }, [groupWorkspaces]);

  useEffect(() => {
    if (userGroup && !!workspaces.length) {
      if (!ObjectUtils.equalObjects(workspaces, updatedWorkspacesRef.current)) {
        updatedWorkspacesRef.current = structuredClone(workspaces || []);
        setGroupWorkspaces(updatedWorkspacesRef.current);
      }
    }
  // eslint-disable-next-line
  }, [workspaces]);

  return { workspacesLoaded };
};

export default useWorkspacesEffects;